import React from 'react';
import { Filter, UserPlus, Users, Plus, Star, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

/**
 * Filter Section for adding contacts by category, location, and rating
 */
export const RecipientFilterSection = ({
  filterCategory,
  setFilterCategory,
  filterLocation,
  setFilterLocation,
  filterMinRating,
  setFilterMinRating,
  categories,
  locations,
  filteredCount,
  onAddFiltered,
  testIdPrefix = ''
}) => (
  <Card className="border-slate-200 bg-slate-50">
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <Filter className="w-4 h-4" />
        Add Contacts by Filter
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className="text-sm">Category</Label>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}filter-category`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.filter(cat => cat && cat.trim() !== "").map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm">Location</Label>
          <Select value={filterLocation} onValueChange={setFilterLocation}>
            <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}filter-location`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.filter(loc => loc && loc.trim() !== "").map((loc) => (
                <SelectItem key={loc} value={loc}>{loc}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm">Minimum Rating</Label>
          <Select value={filterMinRating} onValueChange={setFilterMinRating}>
            <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}filter-rating`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any Rating</SelectItem>
              <SelectItem value="3">3+ Stars</SelectItem>
              <SelectItem value="3.5">3.5+ Stars</SelectItem>
              <SelectItem value="4">4+ Stars</SelectItem>
              <SelectItem value="4.5">4.5+ Stars</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center justify-between pt-2">
        <p className="text-sm text-slate-600">
          <span className="font-medium">{filteredCount}</span> contacts match this filter
        </p>
        <Button
          onClick={onAddFiltered}
          disabled={filteredCount === 0}
          variant="outline"
          className="border-orange-300 text-orange-600 hover:bg-orange-50"
          data-testid={`${testIdPrefix}add-filtered-group-btn`}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add {filteredCount} to Selection
        </Button>
      </div>
    </CardContent>
  </Card>
);

/**
 * Individual Contact Search and Selection
 */
export const IndividualContactPicker = ({
  searchValue,
  onSearchChange,
  searchedContacts,
  onAddContact,
  testIdPrefix = ''
}) => (
  <Card className="border-slate-200">
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <UserPlus className="w-4 h-4" />
        Add Individual Contacts
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="relative">
        <Input
          placeholder="Search by name, email, or business..."
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pr-10"
          data-testid={`${testIdPrefix}individual-search`}
        />
        {searchValue && (
          <button
            onClick={() => onSearchChange("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {searchedContacts.length > 0 && (
        <div className="mt-2 border border-slate-200 rounded-md max-h-48 overflow-y-auto">
          {searchedContacts.map((contact) => (
            <div
              key={contact.id}
              className="flex items-center justify-between p-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0"
              onClick={() => onAddContact(contact.id)}
            >
              <div>
                <p className="font-medium text-sm text-slate-900">{contact.business_name || contact.name || 'Unknown'}</p>
                <p className="text-xs text-slate-500">{contact.email} • {contact.location || 'No location'}</p>
              </div>
              <div className="flex items-center gap-2">
                {contact.outscraper_data?.rating && (
                  <span className="flex items-center gap-1 text-xs text-slate-500">
                    <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                    {contact.outscraper_data.rating}
                  </span>
                )}
                <Plus className="w-4 h-4 text-slate-400" />
              </div>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

/**
 * Selected Contacts Display with removal option
 */
export const SelectedContactsList = ({
  selectedContacts,
  onRemoveContact,
  onClearAll
}) => (
  <Card className="border-slate-200">
    <CardHeader className="pb-3">
      <div className="flex items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="w-4 h-4" />
          Selected Contacts ({selectedContacts.length})
        </CardTitle>
        {selectedContacts.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            Clear All
          </Button>
        )}
      </div>
    </CardHeader>
    <CardContent>
      {selectedContacts.length === 0 ? (
        <p className="text-center text-slate-500 py-4">
          No contacts selected. Use the filters above or search to add contacts.
        </p>
      ) : (
        <div className="border border-slate-200 rounded-md max-h-64 overflow-y-auto">
          {selectedContacts.map((contact) => (
            <div
              key={contact.id}
              className="flex items-center justify-between p-2 border-b border-slate-100 last:border-0 hover:bg-slate-50"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-slate-900 truncate">
                  {contact.business_name || contact.name || 'Unknown'}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {contact.email} • {contact.category || 'No category'} • {contact.location || 'No location'}
                </p>
              </div>
              <div className="flex items-center gap-2 ml-2">
                {contact.outscraper_data?.rating && (
                  <span className="flex items-center gap-1 text-xs text-slate-500">
                    <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                    {contact.outscraper_data.rating}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveContact(contact.id)}
                  className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

/**
 * Compact recipient section used in Edit dialog
 */
export const CompactRecipientSection = ({
  contacts,
  selectedContactIds,
  categories,
  locations,
  filterCategory,
  setFilterCategory,
  filterLocation,
  setFilterLocation,
  filterMinRating,
  setFilterMinRating,
  filteredCount,
  onAddFilteredGroup,
  showIndividualPicker,
  setShowIndividualPicker,
  individualSearch,
  setIndividualSearch,
  individualFilteredContacts,
  onToggleContact,
  onRemoveContact,
  onClearAll,
  testIdPrefix = 'edit-'
}) => (
  <Card className="border-slate-200">
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <Users className="w-4 h-4" />
        Recipients ({selectedContactIds.size} selected)
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      {/* Add by Filter */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Filter className="w-4 h-4" />
          Add Contacts by Filter
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger data-testid={`${testIdPrefix}filter-category`}>
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.filter(cat => cat && cat.trim() !== "").map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterLocation} onValueChange={setFilterLocation}>
            <SelectTrigger data-testid={`${testIdPrefix}filter-location`}>
              <SelectValue placeholder="Location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.filter(loc => loc && loc.trim() !== "").map((loc) => (
                <SelectItem key={loc} value={loc}>{loc}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterMinRating} onValueChange={setFilterMinRating}>
            <SelectTrigger data-testid={`${testIdPrefix}filter-rating`}>
              <SelectValue placeholder="Min Rating" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any Rating</SelectItem>
              <SelectItem value="3">3+ Stars</SelectItem>
              <SelectItem value="4">4+ Stars</SelectItem>
              <SelectItem value="4.5">4.5+ Stars</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">
            {filteredCount} contacts match this filter
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={onAddFilteredGroup}
            disabled={filteredCount === 0}
            data-testid={`${testIdPrefix}add-filtered-group-btn`}
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Group
          </Button>
        </div>
      </div>

      {/* Add Individual */}
      <div className="border-t pt-4 space-y-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowIndividualPicker(!showIndividualPicker)}
          className="text-slate-600"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          {showIndividualPicker ? "Hide" : "Add Individual Contacts"}
        </Button>
        
        {showIndividualPicker && (
          <div className="space-y-2">
            <Input
              placeholder="Search contacts..."
              value={individualSearch}
              onChange={(e) => setIndividualSearch(e.target.value)}
              className="h-8"
              data-testid={`${testIdPrefix}individual-search`}
            />
            <div className="max-h-32 overflow-y-auto border rounded-md">
              {individualFilteredContacts.map((contact) => (
                <div
                  key={contact.id}
                  className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer text-sm"
                  onClick={() => onToggleContact(contact.id)}
                >
                  <Checkbox checked={selectedContactIds.has(contact.id)} />
                  <span className="flex-1 truncate">
                    {contact.business_name || contact.name || contact.email}
                  </span>
                  <span className="text-slate-400 text-xs truncate">
                    {contact.location}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Selected Contacts */}
      {selectedContactIds.size > 0 && (
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">
              Selected Contacts ({selectedContactIds.size})
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearAll}
              className="text-slate-500 hover:text-red-600 h-6 text-xs"
            >
              Clear All
            </Button>
          </div>
          <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
            {Array.from(selectedContactIds).slice(0, 50).map((contactId) => {
              const contact = contacts.find(c => c.id === contactId);
              if (!contact) return null;
              return (
                <span
                  key={contactId}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded text-xs"
                >
                  {contact.business_name || contact.name || contact.email}
                  <button
                    onClick={() => onRemoveContact(contactId)}
                    className="hover:text-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
            {selectedContactIds.size > 50 && (
              <span className="text-xs text-slate-500">
                +{selectedContactIds.size - 50} more
              </span>
            )}
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);

export default {
  RecipientFilterSection,
  IndividualContactPicker,
  SelectedContactsList,
  CompactRecipientSection
};
